import { FC, useEffect, useState, useMemo, ChangeEvent } from 'react';
import { Column, useTable } from 'react-table';
import { User } from '../../../models/user';
import AdminLeagueService from '../../../services/admin/adminLeague'
import FixtureService from '../../../services/fixture'
import HelperService from '../../../services/helper'
import useNotyf from '../../../context/useNotify';
import { League } from '../../../models/league';
import Dropdown from '../../common/Dropdown';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faEdit, faUserPlus } from "@fortawesome/free-solid-svg-icons";
import ReactPaginate from 'react-paginate';
import { Meta, QueryParams } from '../../../models/misc';
import { StyledPagination, StyledTable } from '.././Styles';
import { Fixture, FixtureUpdateDTO } from '../../../models/fixture';
import ReactModal from '../../common/ReactModal';
import AdminFixtureService from "../../../services/admin/adminFixture"
import TeamService from "../../../services/team"
import LeagueService from "../../../services/league"
import { AdminAddPlayerDTO, Team } from '../../../models/team';
import { formatLiveLinkUrl } from '../../../utils/formatString';
import ConfirmationModalContent from '../../common/ConfirmationModalContent';
import AdminTeamService from "../../../services/admin/adminTeam"

interface Props {
  league: League
  fetchLeague: () => void
}

const TeamTable: FC<Props> = ({ league, fetchLeague }) => {
    const notyf = useNotyf()
    
    // Data
    const [team, setTeam] = useState<Team | null>(null);
    const [searchTerm, setSearchTerm] = useState<string>("");
  
  // API
  const getTeam = async (name: string) => {
    try {
        const filters: QueryParams = {
            page: 1,
            page_size: 1,
            name: name,
        }
        const data = await TeamService.getTeams(filters)
        if (!data.data.length) {
            notyf.error("Couldn't find team.")
        }
        setTeam(data.data[0]);
  } catch (err) {
    console.log("Error fetching team: ", err);
    HelperService.showNotfyErr(notyf, err, "Error fetching team.");
  }
};
  
  const addTeam = async (teamId: string) => {
      try {
        await AdminLeagueService.AddTeamToLeague(league._id, teamId)
        notyf.success("Team successfully added.")
        return true
      } catch (err) {
          console.log("Error adding teams: ", err);
          HelperService.showNotfyErr(notyf, err, "Error adding teams.");
      }
  }

  const removeTeam = async (teamId: string) => {
      try {
          await AdminLeagueService.RemoveTeamFromLeague(league._id, teamId)
          notyf.success("Team successfully removed.")
      } catch (err) {
          console.log("Error removing teams: ", err);
          HelperService.showNotfyErr(notyf, err, "Error removing teams.");
      }
  }

  const addPlayer = async (teamId: string, AdminAddPlayerDTO: AdminAddPlayerDTO) => {
    try {
      await AdminTeamService.addPlayer(teamId, AdminAddPlayerDTO)
      notyf.success("Player added.")
      fetchLeague()
    } catch (err) {
      console.log("Error adding player: ", err);
      HelperService.showNotfyErr(notyf, err, "Error adding player.");
    }
  }
  
  // Methods

  const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleSearchButtonClick = () => {
    getTeam(searchTerm);
  };

  const handleClickAdd = async (team: Team) => {
    const isAdded = await addTeam(team._id);
    setTeam(null)
    fetchLeague();
  };

  const handleClickAddPlayer = (team: Team) => {
    setTeamToAddPlayer(team);
    setShowAddPlayerModal(true);
  };

    // Table data
    const columns: Column<Team>[] = useMemo(
        () => [
          {
            Header: 'Name',
            accessor: 'name',
            Cell: ({ row }) => (
              <div className="d-flex align-items-center">
                <img
                  src={row.original.image}
                  alt={row.original.name}
                  className="img-fluid me-2" // Bootstrap classes for responsive image and margin
                  style={{ maxWidth: '30px', maxHeight: '30px', borderRadius: '50%' }} // Adjust size and make it circular
                />
                {row.original.name}
              </div>
            ),
        },
        {
          Header: 'Live Link',
          accessor: 'liveLink',
          Cell: ({ row }) => {
            const formattedLink = formatLiveLinkUrl(row.original.liveLink);
            return (
              <a href={formattedLink.url} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-reset">
                {formattedLink.displayName}
              </a>
            );
          },
        },
            {
            Header: 'Players',
            accessor: 'roster',
            Cell: ({ cell: { value } }) => (
                <span>{value ? value.length : 0}</span>
                ),
            },
          {
            Header: 'Actions',
            accessor: (team: Team) => team,
            Cell: ({ cell: { value: team } }: { cell: { value: Team } }) => (
              <div className="actions d-flex">
                <button className='btn btn-danger btn-sm me-2' onClick={() => handleShowRemoveTeamModal(team)}>
                <FontAwesomeIcon icon={faTrashAlt} /> Remove
                </button>
                <button className='btn btn-primary btn-sm' onClick={() => handleClickAddPlayer(team)}>
                  <FontAwesomeIcon icon={faUserPlus} /> Add Player
                </button>
              </div>
            ),
            className: 'actions',
            width: 150
          },
        ],
        [team]
      );
      const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
      } = useTable({ columns, data: league.teams });
  
  // Modals

  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);

  const closeModals = () => {
    setSelectedTeam(null);
    setShowRemoveTeamModal(false);
    setShowAddPlayerModal(false);
    setTeamToAddPlayer(null);
    setPlayerToAdd({ username: "", seasonsRemaining: 5 });
  };

  // Modal - remove team
  const [showRemoveTeamModal, setShowRemoveTeamModal] = useState(false);

  const handleShowRemoveTeamModal = (team: Team) => {
    setSelectedTeam(team);
    setShowRemoveTeamModal(true);
  };

  const handleClickRemoveTeam = async () => {
    if (selectedTeam) {
      await removeTeam(selectedTeam._id);
    }
    closeModals();
    fetchLeague()
  }

    // Modal - Add Player
    const [showAddPlayerModal, setShowAddPlayerModal] = useState<boolean>(false);
    const [teamToAddPlayer, setTeamToAddPlayer] = useState<Team | null>(null);
    const [playerToAdd, setPlayerToAdd] = useState<AdminAddPlayerDTO>({ username: "", seasonsRemaining: 5 });
    
    const handleAddPlayerInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setPlayerToAdd({ ...playerToAdd, [name]: value });
    };

    const handleAddPlayerSubmit = async (e: any) => {
      e.preventDefault();
      if (teamToAddPlayer && playerToAdd) {
        await addPlayer(teamToAddPlayer._id, playerToAdd);
      }
      closeModals();
    };


  return (
      <>
        {/* Add a new team */}
      <div className='d-flex flex-column'>
      <div className="d-flex flex-wrap">
        <div className="col-12 col-md-3 p-2">
            <input 
              type="text" 
              className="form-control bg-dark text-white" 
              placeholder="Add a team..." 
              value={searchTerm}
              onChange={handleSearchInputChange}
            />
          </div>
          <div className="col-12 col-md-3 py-2">
            <button 
              className="btn btn-primary" 
              onClick={handleSearchButtonClick}
            >
              Search
            </button>
            {team && (
                <button 
                  className="btn btn-success btn-sm p-2 ms-2" 
                  onClick={() => handleClickAdd(team)}
                >
                  Add Team
                </button>
            )}
            </div>

          </div>
        </div>
        {/* Table */}
         <div className='p-2'>
            <div className="table-responsive">
          <StyledTable {...getTableProps()}>
            <thead>
              {headerGroups.map(headerGroup => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map(column => (
                    <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map(row => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map(cell => {
                      return (
                        <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </StyledTable>
        </div>
      </div>
      
      {/* Modal - Remove Team */}
      <ReactModal
        modalOpen={showRemoveTeamModal}
        component={<ConfirmationModalContent
          onConfirm={handleClickRemoveTeam}
          onCancel={() => closeModals()}
          message={`Are you sure you want to remove ${selectedTeam?.name}?`}
        />}
        closeModal={() => closeModals()}
      />

        {/* Add Player Modal */}
  <ReactModal
    modalOpen={showAddPlayerModal}
    closeModal={() => closeModals()}
    component={
      <>
        <form onSubmit={handleAddPlayerSubmit} className='m-5'>
          <h3 className='mb-4'>Add Player to {teamToAddPlayer?.name}</h3>
          <div className="mb-3">
            <label htmlFor="username" className="form-label">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              className="form-control"
              value={playerToAdd.username}
              onChange={handleAddPlayerInputChange}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="seasonsRemaining" className="form-label">Seasons Remaining</label>
            <input
              type="number"
              id="seasonsRemaining"
              name="seasonsRemaining"
              className="form-control"
              value={playerToAdd.seasonsRemaining}
              onChange={handleAddPlayerInputChange}
              min="1"
              required
            />
          </div>
          <div className="d-flex justify-content-end">
            <button type="button" className="btn btn-secondary me-2" onClick={() => closeModals()}>Cancel</button>
            <button type="submit" className="btn btn-primary">Add Player</button>
          </div>
        </form>
      </>
    }
  />
        
    </>
  );
};

export default TeamTable;
